import React from 'react';
import PropTypes from 'prop-types';
import {Nav, Navbar} from "react-bootstrap";
import {Link, NavLink} from "react-router-dom";
import APP_VERSION from "../../version";
import { getCurrentSchoolYearStartYear } from "../common/schoolYear";
import { buildManageRouteWithSchoolYear } from "./manageSchoolYearRouting";
import SchoolYearSelector from "./SchoolYearSelector";
import LanguageSwitcher from '../common/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const getItemIcon = item => item.icon || "far fa-circle";

function ManageNav({
  items,
  brandName,
  signOut,
  currentSearch,
  activeSchoolYearStartYear,
  schoolYearOptions,
  onActiveSchoolYearChange,
  settingsRoute,
  permissionsRoute,
  canSwitchContext,
  onSwitchContext,
  contextSwitching,
}) {
  const { t: tr } = useTranslation();
  return (
    <Navbar collapseOnSelect expand="lg" id="nav" className="manage-custom-nav fixed-top px-4 py-1 shadow-sm">
      <Navbar.Brand as={Link} to="/" className="manage-custom-nav-brand p-1">
        <img
          src="../../resources/app/manage-logo.png"
          width="52px"
          height="52px"
          className="manage-custom-nav-logo"
          alt=""
        />
        <span className="manage-custom-nav-brand-text">{brandName}</span>
      </Navbar.Brand>
      <LanguageSwitcher />
      {!contextSwitching && (
        <>
          <Navbar.Toggle label={tr("common:toggleNavigation")} aria-controls="responsive-navbar-nav" />
          <Navbar.Collapse id="responsive-navbar-nav">
            <Nav className="manage-custom-nav-items mr-auto">
              {items.map(item => (
                <Nav.Link
                  as={NavLink}
                  exact={item.exact || item.route === "/manage/"}
                  activeClassName="active"
                  eventKey={item.route}
                  key={item.route}
                  to={buildManageRouteWithSchoolYear(item.route, currentSearch, activeSchoolYearStartYear)}
                  className="manage-custom-nav-link ml-1 my-0"
                >
                  <i className={`${getItemIcon(item)} manage-custom-nav-link-icon`} />
                  {item.name}
                </Nav.Link>
              ))}
            </Nav>
            <Nav className="manage-custom-nav-meta align-items-lg-center ml-lg-2">
              <Navbar.Text className="manage-version-label ml-1 mr-lg-3 my-0">
                {APP_VERSION}
              </Navbar.Text>
              <div className="manage-school-year-selector-wrapper mr-lg-3 my-2 my-lg-0">
                <SchoolYearSelector
                  activeSchoolYearStartYear={activeSchoolYearStartYear}
                  options={schoolYearOptions}
                  onChange={onActiveSchoolYearChange}
                />
              </div>
              {permissionsRoute && (
                <Nav.Link
                  as={NavLink}
                  exact
                  activeClassName="active"
                  eventKey="permissions"
                  to={buildManageRouteWithSchoolYear(permissionsRoute, currentSearch, activeSchoolYearStartYear)}
                  title={tr("common:permissions")}
                  aria-label={tr("common:permissions")}
                  className="manage-custom-nav-link ml-1 my-0"
                >
                  <i className="fas fa-user-shield fa-lg manage-custom-nav-link-icon" />
                  <span className="manage-nav-action-label">{tr("common:permissions")}</span>
                </Nav.Link>
              )}
              {settingsRoute && (
                <Nav.Link
                  as={NavLink}
                  exact
                  activeClassName="active"
                  eventKey="settings"
                  to={buildManageRouteWithSchoolYear(settingsRoute, currentSearch, activeSchoolYearStartYear)}
                  title={tr("common:settings")}
                  aria-label={tr("common:settings")}
                  className="manage-custom-nav-link ml-1 my-0"
                >
                  <i className="fas fa-cog fa-lg manage-custom-nav-link-icon" />
                  <span className="manage-nav-action-label">{tr("common:settings")}</span>
                </Nav.Link>
              )}
              {canSwitchContext && (
                <Nav.Link
                  as="button"
                  type="button"
                  eventKey="switchAccount"
                  onClick={onSwitchContext}
                  title={tr("common:switchAccount")}
                  aria-label={tr("common:switchAccount")}
                  className="manage-custom-nav-link ml-1 my-0 btn btn-link"
                >
                  <i className="fas fa-random fa-lg manage-custom-nav-link-icon" />
                  <span className="manage-nav-action-label">{tr("common:switchAccount")}</span>
                </Nav.Link>
              )}
              <Nav.Link
                as="button"
                type="button"
                eventKey="signOut"
                onClick={signOut}
                title={tr("common:signOut")}
                aria-label={tr("common:signOut")}
                className="manage-custom-nav-link ml-1 my-0 btn btn-link"
              >
                <i className="fas fa-sign-in-alt fa-lg manage-custom-nav-link-icon" />
                <span className="manage-nav-action-label">{tr("common:signOut")}</span>
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </>
      )}
    </Navbar>
  );
}

ManageNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    selected: PropTypes.bool,
    route: PropTypes.string,
    icon: PropTypes.string,
    exact: PropTypes.bool,
    onClick: PropTypes.func,
  })).isRequired,
  signOut: PropTypes.func.isRequired,
  brandName: PropTypes.string,
  currentSearch: PropTypes.string,
  activeSchoolYearStartYear: PropTypes.number,
  schoolYearOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number.isRequired,
    text: PropTypes.string.isRequired,
  })),
  onActiveSchoolYearChange: PropTypes.func,
  settingsRoute: PropTypes.string,
  permissionsRoute: PropTypes.string,
  canSwitchContext: PropTypes.bool,
  onSwitchContext: PropTypes.func,
  contextSwitching: PropTypes.bool,
};

ManageNav.defaultProps = {
  currentSearch: "",
  brandName: "Extra English",
  activeSchoolYearStartYear: getCurrentSchoolYearStartYear(),
  schoolYearOptions: [],
  onActiveSchoolYearChange: () => {},
  settingsRoute: null,
  permissionsRoute: null,
  canSwitchContext: false,
  onSwitchContext: () => {},
  contextSwitching: false,
};

export default ManageNav;
